import { getTheWordClinic } from "../neoClinicWord";
import type { EhrLabDocumentRef } from "../labDocumentRef";
import { mergeNeoSourceCandidate } from "../sourceCandidateMerge";
import { patientProfileBaseHref } from "../sourceNavigation";

// ── Source citations ────────────────────────────────────────────────────
//
// Neo's assistant text can carry inline ``[[source:<source_id>]]`` markers
// (e.g. ``[[source:note:pcc:107973010]]``). The backend never persists a
// dedicated "sources" block — instead every emitting tool attaches one or
// more **source-candidate** blobs to its result, both at the response level
// (``output.sources[]``) and, for list-shaped tools, mirrored per row
// (``output.items[i].source``). See
// ``Patient-Centric-App-V2/app/helper/Neo/tools/_citation_sources.py`` for
// the producer half of this contract and ``NEO_CITATION_TYPES.md`` for the
// full type catalogue.
//
// The frontend rebuilds a ``source_id → NeoSource`` lookup from those
// tool-call outputs (``buildSourceCacheFromToolOutputs``), turns the markers
// into clickable chips (``processHtmlSources`` in ``./sourceMarkers``), and
// opens a drawer (``./SourceDrawer``) with a "jump to record" action keyed
// off ``source_type`` (``buildSourceHash``). Shared by the Neo page and the
// ui2026 Copilot chat so both resolve and render citations identically.

/** Navigation hint emitted by the backend candidate builders. Only
 *  ``record_id`` is reliably consumed by the frontend — ``tab`` is advisory
 *  (it carries backend tab identifiers, not cip-app route slugs), so route
 *  selection is driven off ``source_type`` instead. */
export interface NeoSourceTarget {
  entity: string;
  tab: string;
  record_id: number | string;
  patient_id?: number;
  clinic_id?: number;
  highlight?: unknown;
  subsection?: string;
}

/** One citable source candidate. Mirrors the dict shape returned by every
 *  ``build_<entity>_source`` helper on the backend. */
export interface NeoSource {
  source_id: string;
  source_type: string;
  title: string;
  /** A concrete excerpt of the source record, ONLY when a backend builder
   *  deliberately attached one (e.g. a note body). Never synthesised from
   *  aggregated tool output — a citation is a source of truth, not a re-run
   *  of the tool's analytics. */
  snippet?: string;
  patient_id?: number;
  clinic_id?: number;
  /** Lets the drawer label which resident a cross-patient citation belongs
   *  to (e.g. "Jane Doe (MRN 1234567)"). */
  patient_display?: string;
  target: NeoSourceTarget;
  /** Opaque application reference for an authenticated, just-in-time
   * download. It never contains an S3 key, presigned URL, or file bytes. */
  document_ref?: EhrLabDocumentRef;
}

// ── Source-candidate collection ─────────────────────────────────────────

/** True when ``value`` looks like a source-candidate blob (has a non-empty
 *  ``source_id`` string and a ``target`` object). */
const isNeoSourceCandidate = (value: unknown): value is NeoSource => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.source_id === "string" &&
    v.source_id.length > 0 &&
    typeof v.target === "object" &&
    v.target !== null
  );
};

/** Recursively harvest source candidates out of an arbitrary tool-call
 *  output. Mirrors the backend collector: candidates live under a per-row
 *  ``source`` key and/or a top-level/nested ``sources[]`` array. We also
 *  walk every nested value so deeply-nested shapes (e.g.
 *  ``matches[i].sources``) are caught without per-tool special-casing. When
 *  the same ``source_id`` appears twice we keep the richer copy — the one
 *  carrying a snippet / patient_id wins. */
const strTrim = (v: unknown): string | undefined =>
  typeof v === "string" && v.trim() ? v.trim() : undefined;

const collectSourcesInto = (
  node: unknown,
  sink: Record<string, NeoSource>
): void => {
  if (!node || typeof node !== "object") return;
  if (Array.isArray(node)) {
    for (const child of node) collectSourcesInto(child, sink);
    return;
  }
  const obj = node as Record<string, unknown>;
  const consider = (cand: unknown) => {
    if (!isNeoSourceCandidate(cand)) return;
    const existing = sink[cand.source_id];
    // Duplicate candidates are often complementary: a list row may contain
    // the excerpt while a top-level source carries download/navigation data.
    sink[cand.source_id] = mergeNeoSourceCandidate(existing, cand);
  };
  consider(obj.source);
  if (Array.isArray(obj.sources)) {
    for (const s of obj.sources) consider(s);
  }
  for (const value of Object.values(obj)) collectSourcesInto(value, sink);
};

const toPosInt = (value: unknown): number | undefined => {
  const n =
    typeof value === "number"
      ? value
      : typeof value === "string"
      ? Number(value)
      : NaN;
  return Number.isInteger(n) && n > 0 ? n : undefined;
};

/** Add a derived ``patient:<id>`` anchor. Mirrors the backend's
 *  ``_add_patient_anchor``: never clobbers an explicit/display-bearing
 *  candidate, but will upgrade a bare anchor once a display name is found. */
const addPatientAnchor = (
  sink: Record<string, NeoSource>,
  patientId: unknown,
  clinicId?: unknown,
  display?: unknown
): void => {
  const pid = toPosInt(patientId);
  if (pid == null) return;
  const sid = `patient:${pid}`;
  const existing = sink[sid];
  if (existing?.patient_display) return;
  const cid = toPosInt(clinicId);
  const disp = strTrim(display);
  if (existing && !disp) return;
  sink[sid] = {
    source_id: sid,
    source_type: "patient",
    title: disp ?? `Patient #${pid}`,
    patient_id: pid,
    ...(cid != null ? { clinic_id: cid } : {}),
    ...(disp ? { patient_display: disp } : {}),
    target: {
      entity: "patient",
      tab: "profile",
      record_id: pid,
      patient_id: pid,
      ...(cid != null ? { clinic_id: cid } : {}),
    },
  };
};

/** Add a derived ``clinic:<id>`` anchor (first-write-wins, like the
 *  backend's ``_add_clinic_anchor``). */
const addClinicAnchor = (
  sink: Record<string, NeoSource>,
  clinicId: unknown,
  display?: unknown
): void => {
  const cid = toPosInt(clinicId);
  if (cid == null) return;
  const sid = `clinic:${cid}`;
  if (sink[sid]) return;
  const disp =
    typeof display === "string" && display.trim() ? display.trim() : undefined;
  sink[sid] = {
    source_id: sid,
    source_type: "clinic",
    title: disp ?? `${getTheWordClinic({ capitalized: true })} #${cid}`,
    clinic_id: cid,
    target: {
      entity: "clinic",
      tab: "dashboard",
      record_id: cid,
      clinic_id: cid,
    },
  };
};

/** Recover ``patient:<id>`` / ``clinic:<id>`` anchors from the patient/clinic
 *  identity a tool output already exposes (entity ``ref``, ``links`` edges,
 *  hoisted ``patient_id`` / ``clinic_id`` scalars, per-row ``fields``).
 *
 *  This mirrors the backend's ``source_artifacts._derive_scope_candidates``.
 *  It's required because the backend derives these "auxiliary" anchors only
 *  inside its verifier — they let the model's ``[[source:patient:…]]`` /
 *  ``[[source:clinic:…]]`` markers pass verification, but are NEVER written
 *  back into the tool-output payload the frontend receives. Without
 *  re-deriving them here, those (very common) citations render as chips that
 *  resolve to nothing and do nothing on click. Skips error envelopes so an
 *  ``out_of_scope`` / ``not_found`` ref never becomes a citable anchor. */
/** True when a list item is ITSELF a compact ref rather than a row envelope
 *  (mirrors ``_common.is_bare_row_ref`` on the backend). */
const isBareRowRef = (entry: unknown): boolean =>
  !!entry &&
  typeof entry === "object" &&
  !Array.isArray(entry) &&
  "id" in (entry as Record<string, unknown>) &&
  !("type" in (entry as Record<string, unknown>)) &&
  !("ref" in (entry as Record<string, unknown>)) &&
  !("fields" in (entry as Record<string, unknown>));

/** Register the anchor implied by one entity ref, using the ref's own
 *  ``type`` when present and the inherited ``default_ref_type`` otherwise. */
const addRefAnchor = (
  sink: Record<string, NeoSource>,
  r: Record<string, unknown>,
  refType: unknown
): void => {
  if (refType === "patient") {
    addPatientAnchor(sink, r.id, r.clinic_id, r.display);
  } else if (refType === "clinic") {
    addClinicAnchor(sink, r.id, r.display);
  } else if (r.clinic_id != null) {
    addClinicAnchor(sink, r.clinic_id);
  }
};

const deriveScopeAnchorsInto = (
  node: unknown,
  sink: Record<string, NeoSource>,
  defaultRefType?: string
): void => {
  if (Array.isArray(node)) {
    for (const entry of node)
      deriveScopeAnchorsInto(entry, sink, defaultRefType);
    return;
  }
  if (!node || typeof node !== "object") return;
  const obj = node as Record<string, unknown>;
  if (obj.error) return;

  // Compact per-row refs omit ``type``; the enclosing list / envelope
  // declares it once via ``default_ref_type`` (backend `make_row_ref`).
  if (typeof obj.default_ref_type === "string" && obj.default_ref_type) {
    defaultRefType = obj.default_ref_type;
  }

  const ref = obj.ref;
  if (ref && typeof ref === "object" && !Array.isArray(ref)) {
    const r = ref as Record<string, unknown>;
    addRefAnchor(sink, r, "type" in r ? r.type : defaultRefType);
  }

  // A few rosters emit their rows as bare refs rather than wrapping each
  // one in a ``{ ref: … }`` envelope; those inherit the declared type too.
  if (defaultRefType && Array.isArray(obj.items)) {
    for (const entry of obj.items) {
      if (isBareRowRef(entry)) {
        addRefAnchor(sink, entry as Record<string, unknown>, defaultRefType);
      }
    }
  }

  const links = obj.links;
  if (links && typeof links === "object" && !Array.isArray(links)) {
    const l = links as Record<string, unknown>;
    const p = l.patient;
    if (p && typeof p === "object" && !Array.isArray(p)) {
      const pp = p as Record<string, unknown>;
      addPatientAnchor(sink, pp.id, pp.clinic_id, pp.display);
    }
    const c = l.clinic;
    if (c && typeof c === "object" && !Array.isArray(c)) {
      const cc = c as Record<string, unknown>;
      addClinicAnchor(sink, cc.id, cc.display);
    }
  }

  if ("patient_id" in obj) {
    addPatientAnchor(
      sink,
      obj.patient_id,
      obj.clinic_id,
      obj.patient_display ?? obj.patient_name
    );
  }
  if ("clinic_id" in obj) {
    addClinicAnchor(sink, obj.clinic_id, obj.clinic_name);
  }

  const fields = obj.fields;
  if (fields && typeof fields === "object" && !Array.isArray(fields)) {
    const f = fields as Record<string, unknown>;
    if ("patient_id" in f) {
      addPatientAnchor(
        sink,
        f.patient_id,
        f.clinic_id,
        f.patient_display ?? f.patient_name
      );
    }
  }

  for (const [key, val] of Object.entries(obj)) {
    if (key === "links" || key === "source" || key === "sources") continue;
    if (val && typeof val === "object") {
      deriveScopeAnchorsInto(val, sink, defaultRefType);
    }
  }
};

/** Build a ``source_id → NeoSource`` lookup from a list of tool-call
 *  outputs (in transcript order).
 *
 *  Two passes, mirroring the backend collector: first harvest the explicit
 *  record-grained candidates (so they always win on id collision), then
 *  derive the auxiliary ``patient:`` / ``clinic:`` anchors the backend only
 *  computes server-side. */
export const buildSourceCacheFromToolOutputs = (
  outputs: readonly unknown[]
): Record<string, NeoSource> => {
  const sink: Record<string, NeoSource> = {};
  for (const output of outputs) collectSourcesInto(output, sink);
  for (const output of outputs) deriveScopeAnchorsInto(output, sink);
  return sink;
};

// ── Source → route navigation ───────────────────────────────────────────

// Patient citations include ``/clinic/:clinicId`` when the source provides it,
// allowing the profile to offer an explicit facility switch before loading.
// It selects its tab from a ``?tab=<key>`` query param (read by
// ``PatientProfile/overview``), NOT a path segment, so a citation that wants a
// specific tab appends the query rather than ``/<tab>``. These keys mirror the
// ``Tabs.TabPane key=`` values in ``src/pages/PatientProfile/overview``.
const PATIENT_PROFILE_TAB_BY_TYPE: Record<string, string> = {
  alert: "circadiaClinicalTab", // Flags
  discharge_medrec: "medicationsTab", // Med Rec (the whole reconciliation)
};

// EHR-backed records aren't top-level tabs — they're sub-listings inside the
// PointClickCare tab (``ehrDataTab``), pre-selected via ``?subsection=``
// (read by ``PatientProfile/overview``). Values match ``EHR_SUBSECTION_NAMES``
// there. NOTE: ``medication:`` is a Medications sub-listing here, distinct from
// ``discharge_medrec:`` which is the separate Med Rec tab.
const EHR_SUBSECTION_BY_TYPE: Record<string, string> = {
  medication: "Medications",
  lab: "Labs",
  assessment: "Assessments",
  careplan: "Care Plans",
  nutrition_order: "Nutrition",
};

export const SOURCE_KIND_LABEL: Record<string, string> = {
  patient: "Patient profile",
  note: "Clinical note",
  alert: "Clinical flag",
  lab: "Lab report",
  medication: "Medication",
  assessment: "Assessment",
  careplan: "Care plan",
  nutrition_order: "Nutrition order",
  discharge_medrec: "Discharge medication reconciliation",
  // A getter so the wording re-resolves once the care setting loads; this map
  // is a module-level constant built at import time.
  get clinic() {
    return getTheWordClinic({ capitalized: true });
  },
};

/** Build the ``?labId``/``?labReportId`` query fragment for a lab citation.
 *
 *  Mirror-backed labs have a numeric primary key and open by ``labId``. A
 *  report that lives only in PointClickCare has no local numeric id and is
 *  addressed by its (non-numeric) ``report_id`` — those open by
 *  ``labReportId`` so the destination can load them via the by-report detail
 *  endpoint. (A PCC report_id that is coincidentally all-digits is
 *  indistinguishable here and falls back to ``labId``; that ambiguity lives in
 *  the backend citation contract, not this builder.) */
const labDeepLinkParam = (recordId: number | string | undefined): string => {
  if (recordId == null) return "";
  const raw = String(recordId);
  if (!raw) return "";
  const numeric = Number(raw);
  return Number.isInteger(numeric) && numeric > 0
    ? `&labId=${encodeURIComponent(raw)}`
    : `&labReportId=${encodeURIComponent(raw)}`;
};

/** Build the in-app hash-route a citation should navigate to. Route + tab
 *  selection is driven by ``source_type`` (cip-app owns its own routing),
 *  while ``record_id`` rides along as a ``?highlight=`` param so the
 *  destination page can scroll to / emphasise the cited record. Returns
 *  ``null`` when the citation lacks the identifiers needed to navigate. */
export const buildSourceHash = (src: NeoSource): string | null => {
  const t = src.target ?? ({} as NeoSourceTarget);
  const patientId = src.patient_id ?? t.patient_id;
  const clinicId = src.clinic_id ?? t.clinic_id;

  // Facility-level citations open the clinic dashboard, not a patient page.
  if (src.source_type === "clinic") {
    return clinicId ? `#/dashboard?clinic=${clinicId}` : null;
  }
  if (!patientId) return null;

  const base = patientProfileBaseHref(patientId, clinicId);

  // ``note:<kind>:<id>`` — IPV notes live on the Circadia Progress Notes tab;
  // PCC notes live under PointClickCare → Notes.
  if (src.source_type === "note") {
    const kind = src.source_id.split(":")[1] ?? "pcc";
    const noteId = t.record_id ?? src.source_id.split(":")[2];
    const highlight =
      noteId != null ? `&highlight=${encodeURIComponent(String(noteId))}` : "";
    if (kind === "ipv") {
      return `${base}?tab=circadiaProgressNotesTab&notesSource=Circadia${highlight}`;
    }
    return `${base}?tab=ehrDataTab&subsection=${encodeURIComponent(
      "Notes"
    )}${highlight}`;
  }

  // EHR-backed records open the PointClickCare tab and pre-select their
  // sub-listing (Medications, Labs, …).
  const subsection = EHR_SUBSECTION_BY_TYPE[src.source_type];
  if (subsection) {
    const recordParam =
      src.source_type === "lab" ? labDeepLinkParam(t.record_id) : "";
    return `${base}?tab=ehrDataTab&subsection=${encodeURIComponent(
      subsection
    )}${recordParam}`;
  }

  const tab = PATIENT_PROFILE_TAB_BY_TYPE[src.source_type];
  return tab ? `${base}?tab=${tab}` : base;
};

/** Parse a citation snippet that may be raw text or a JSON note-body array
 *  (``[{name, value}]`` — the shape PCC ``body_preview`` uses). Returns
 *  human-readable text, normalising CR/LF so ``white-space: pre-wrap`` can
 *  render line breaks. Non-JSON snippets pass through unchanged. */
export const parseSourceSnippet = (raw: string): string => {
  const s = raw.replace(/^…+/, "").replace(/…+$/, "").trim();
  let text = s;
  try {
    const safeJson = s
      .replace(/\r\n/g, "\\r\\n")
      .replace(/\r/g, "\\r")
      .replace(/\n/g, "\\n");
    const parsed = JSON.parse(safeJson);
    if (Array.isArray(parsed)) {
      text = parsed
        .map((p: { name?: string; value?: string }) => p.value ?? "")
        .join("\n\n")
        .trim();
    }
  } catch {
    // Not JSON — use as-is.
  }
  return text.replace(/\r\n/g, "\n").replace(/\r/g, "\n");
};

/** The link label for a single source's "go verify" button. */
export const sourceCtaLabel = (type: string): string =>
  type === "clinic"
    ? `Open ${getTheWordClinic()} dashboard`
    : type === "patient"
    ? "Open patient profile"
    : "Open in patient profile";
